import { useEffect, useRef } from "preact/hooks";
import type { SnackbarState } from "../hooks/useDue";
import { registerActionSnackbarVisible } from "../hooks/useSnackbarPresence";

export function Snackbar({
  state,
  onDismiss,
}: {
  state: SnackbarState | null;
  onDismiss: () => void;
}) {
  const undoRef = useRef<HTMLButtonElement>(null);
  const alerts = state?.alerts ?? [];
  const hasAction = Boolean(state?.onUndo) || alerts.length > 0;

  // Focus management (design-spec-today-card.md §5): move focus to Undo
  // when it appears so a screen-reader / keyboard user can act on it
  // immediately, without hunting for it. (A persistent failure alert is
  // announced by its own `role="alert"` instead — it never steals focus
  // from whatever row the person is tapping next.)
  useEffect(() => {
    if (state?.onUndo) undoRef.current?.focus();
  }, [state?.onUndo]);

  // QA-B1 fix (design-phase-c.md §5.2 note) — while THIS instance is
  // showing an actionable Undo (or a "Try again" alert), tell the
  // app-wide signal the global Quick Add FAB reads so it can get out of
  // the way. A plain message-only toast never moves the FAB.
  useEffect(() => {
    if (!hasAction) return;
    return registerActionSnackbarVisible();
  }, [hasAction]);

  if (!state) return null;

  return (
    <div class="snackbar-stack">
      {/* UX review (done-feedback) — a save that permanently failed. Never
       * auto-dismissed (only "Try again" or "Dismiss" clears it) and
       * announced assertively: a water check that LOOKS done but wasn't
       * saved is the worst failure this app can have. */}
      {alerts.map((alert) => (
        <div key={alert.key} class="snackbar is-error" role="alert">
          <span>{alert.message}</span>
          <span class="snackbar-actions">
            <button type="button" onClick={alert.onAction}>
              {alert.actionLabel}
            </button>
            {alert.onDismiss ? (
              <button type="button" class="snackbar-dismiss" onClick={alert.onDismiss}>
                Dismiss
              </button>
            ) : null}
          </span>
        </div>
      ))}
      {state.message ? (
        <div class="snackbar" role="status" aria-live="polite">
          <span>{state.message}</span>
          {state.onUndo ? (
            <button
              ref={undoRef}
              type="button"
              onClick={() => {
                state.onUndo?.();
                onDismiss();
              }}
            >
              {state.actionLabel ?? "Undo"}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
